import Anchor, { type AnchorProps } from "@components/controls/Anchor";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { downloadFile } from "@core-ui/downloadResource";
import { AgentSecretsSettingsLink } from "@ext/agent/components/message/AgentSecretsSettingsLink";
import { loadAgentFile } from "@ext/agent/components/message/loadAgentFile";
import { AgentSkillLink } from "@ext/agent/components/skills/AgentSkillLink";
import { LinkAdapter } from "@ext/agent/mcp/parser/adapters/linkAdapter";
import t from "@ext/localization/locale/translate";
import { openFilePreview } from "@ext/markdown/elements/file/edit/logic/openFilePreview";
import { toast } from "@ui-kit/Toast";
import type { MouseEvent, ReactNode } from "react";
import { useRef } from "react";

const AgentChatFileLink = ({ href, children }: { href: string; children: ReactNode }) => {
	const fetchingRef = useRef(false);
	const apiUrlCreator = ApiUrlCreatorService.value;

	const onClickHandler = async (event: MouseEvent<HTMLButtonElement>) => {
		if (fetchingRef.current) return;
		fetchingRef.current = true;

		const onError = () => {
			toast(t("file-not-found"), {
				status: "error",
				icon: "triangle-alert",
				description: t("file-download-error-message"),
				size: "lg",
			});
		};

		try {
			event.preventDefault();
			event.stopPropagation();

			const file = await loadAgentFile(href, apiUrlCreator);
			if (!file) return onError();

			openFilePreview(file.buffer, file.path, {
				onError,
				downloadResource: () =>
					downloadFile(
						file.buffer,
						MimeTypes[file.path.extension] ?? file.path.extension,
						decodeURIComponent(file.path.nameWithExtension),
					),
			});
		} finally {
			fetchingRef.current = false;
		}
	};

	return (
		<button className="link" onClick={onClickHandler} type="button">
			{children}
		</button>
	);
};

export const AgentChatLink = ({ children, href, ...rest }: AnchorProps) => {
	if (href && LinkAdapter.fromAgentFileHref(href)) {
		return <AgentChatFileLink href={href}>{children}</AgentChatFileLink>;
	}

	if (href && LinkAdapter.isSecretsSettingsHref(href)) {
		return <AgentSecretsSettingsLink>{children}</AgentSecretsSettingsLink>;
	}

	const skill = href && LinkAdapter.parseSkillHref(href);
	if (skill) return <AgentSkillLink skillName={skill.skillName}>{children}</AgentSkillLink>;

	if (href && LinkAdapter.isUnopenableAgentHref(href)) return <span>{children}</span>;

	return (
		<Anchor href={href} {...rest}>
			{children}
		</Anchor>
	);
};
